import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, MapPin, Sparkles, Users, Heart, Star, Lock, Smartphone, QrCode, Play, CheckCircle, Check, X, Crown, Globe, Trophy } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import Footer from "@/components/layout/Footer";
import { JHARKHAND_DISTRICTS } from "@/data/jharkhand";
import JHARKHAND_BLOCKS from "@/jharkhand_blocks.json";

const normalizeDistrictName = (d) => {
  const map = {
    "Jamshedpur": "East Singhbhum",
    "Chaibasa": "West Singhbhum",
    "Saraikela": "Seraikela",
    "Koderma": "Kodarma"
  };
  return map[d] || d;
};

function AnimatedCounter({ value, suffix = "" }) {
  const [count, setCount] = useState(0);
  const elementRef = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const duration = 2000; // 2 seconds
          const startTime = performance.now();

          const animate = (currentTime) => {
            const elapsedTime = currentTime - startTime;
            const progress = Math.min(elapsedTime / duration, 1);
            
            // Easing: easeOutQuad
            const easeProgress = 1 - Math.pow(1 - progress, 4);
            const currentCount = Math.floor(easeProgress * value);
            
            setCount(currentCount);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCount(value);
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [value]);

  return <span ref={elementRef}>{count.toLocaleString("en-IN")}{suffix}</span>;
}

export default function Landing() {
  const [phoneEnabled, setPhoneEnabled] = useState(false);
  const [cms, setCms] = useState(null);
  const { language, setLanguage, t } = useLanguage();
  const [contactInput, setContactInput] = useState("");
  const [linkSent, setLinkSent] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [linkSending, setLinkSending] = useState(false);

  // Mitra Application Modal State
  const [mitraModalOpen, setMitraModalOpen] = useState(false);
  const [applyStep, setApplyStep] = useState(1); // 1: details, 2: otp
  const [applyName, setApplyName] = useState("");
  const [applyPhone, setApplyPhone] = useState("");
  const [applyDistrict, setApplyDistrict] = useState(JHARKHAND_DISTRICTS[0] || "Ranchi");
  const [applyBlock, setApplyBlock] = useState("");
  const [applyNotes, setApplyNotes] = useState("");
  const [applyOtp, setApplyOtp] = useState("");
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState("");
  const [applyErr, setApplyErr] = useState("");
  const [applyOtpHint, setApplyOtpHint] = useState("");

  // Sync block list with chosen district
  useEffect(() => {
    const normalized = normalizeDistrictName(applyDistrict);
    const blocks = JHARKHAND_BLOCKS[normalized] || [];
    setApplyBlock(blocks[0] || "");
  }, [applyDistrict]);

  const handleSendMitraOtp = async (e) => {
    e.preventDefault();
    const cleanPhone = applyPhone.replace(/\D/g, "");
    if (!applyName.trim() || !cleanPhone) {
      setApplyErr("Please enter your name and valid WhatsApp number.");
      return;
    }
    if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
      setApplyErr("Please enter a valid 10-digit Indian WhatsApp mobile number (starting with 6, 7, 8, or 9).");
      return;
    }

    setApplying(true);
    setApplyErr("");
    try {
      const { data } = await api.post("/mitra/apply-send-otp", { phone: cleanPhone });
      if (data.already_pending) {
        setApplyMsg(data.message);
      } else {
        if (data.otp_hint) setApplyOtpHint(data.otp_hint);
        setApplyStep(2);
      }
    } catch (err) {
      setApplyErr(err?.response?.data?.detail || "Failed to send OTP. Please check your WhatsApp number.");
    } finally {
      setApplying(false);
    }
  };

  const handleVerifyMitraOtp = async (e) => {
    e.preventDefault();
    if (!applyOtp.trim() || applyOtp.trim().length < 4) {
      setApplyErr("Please enter a valid OTP.");
      return;
    }
    setApplying(true);
    setApplyErr("");
    try {
      const { data } = await api.post("/mitra/apply-verify-otp", {
        name: applyName.trim(),
        phone: applyPhone.trim(),
        district: applyDistrict,
        block: applyBlock,
        notes: applyNotes.trim(),
        otp: applyOtp.trim()
      });
      setApplyMsg(data.message);
    } catch (err) {
      setApplyErr(err?.response?.data?.detail || "Invalid or expired OTP. Please try again.");
    } finally {
      setApplying(false);
    }
  };

  const handleSendLink = async (e) => {
    e.preventDefault();
    const email = contactInput.trim();
    if (!email) {
      setLinkError("Please enter your email address.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setLinkError("Please enter a valid email address.");
      return;
    }
    setLinkSending(true);
    setLinkError("");
    try {
      await api.post("/send-app-link", { email });
      setLinkSent(true);
      setContactInput("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setLinkError(typeof detail === "string" ? detail : "Failed to send email. Please try again.");
    } finally {
      setLinkSending(false);
    }
  };

  const [packages, setPackages] = useState([]);
  const [stats, setStats] = useState({ total_profiles: 12450, districts_covered: 24, blocks_covered: 263 });
  const [blockLeaderboard, setBlockLeaderboard] = useState([]);

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => setPhoneEnabled(data.phone_login_enabled)).catch(() => {});
    api.get("/cms/home").then(({ data }) => setCms(data)).catch(() => {});
    api.get("/packages/public").then(({ data }) => setPackages(data)).catch(() => {});
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/referrals/block-leaderboard").then(({ data }) => setBlockLeaderboard(data || [])).catch(() => {});
  }, []);
  const c = cms || {};
  const featIcons = [MapPin, Users, Star, ShieldCheck];

  const installPWA = (e, forcedPlatform = null) => {
    e.preventDefault();
    if (forcedPlatform === "ios") {
      window.dispatchEvent(new CustomEvent("show-pwa-instructions", { detail: { platform: "ios" } }));
      return;
    }
    if (window._pwaInstallPrompt) {
      window._pwaInstallPrompt.prompt();
    } else {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const targetPlatform = forcedPlatform || (isIOS ? "ios" : "android");
      window.dispatchEvent(new CustomEvent("show-pwa-instructions", { detail: { platform: targetPlatform } }));
    }
  };
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="absolute top-0 inset-x-0 z-30">
        <div className="container-px h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="/logo.png" alt="Hamarsathi" className="h-10 object-contain" />
          </Link>
          <div className="flex items-center gap-2">
            {/* Language Switcher */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === "en" ? "hi" : "en")}
              className="text-white hover:bg-white/10 rounded-full flex items-center gap-1.5"
              data-testid="lang-switch-btn"
            >
              <Globe className="w-4 h-4 text-white/80" />
              <span className="text-xs font-semibold uppercase">{language === "en" ? "हिं" : "EN"}</span>
            </Button>
            <Button asChild variant="ghost" className="text-white hover:bg-white/10 rounded-full" data-testid="landing-login-btn">
              <Link to="/login">{t("nav.login")}</Link>
            </Button>
            <Button asChild className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full" data-testid="landing-register-btn">
              <Link to="/register">{t("nav.register")}</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[640px] lg:min-h-[760px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/35069916/pexels-photo-35069916.jpeg"
               alt="Couple" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2A0A10]/85 via-[#3a0e16]/60 to-transparent" />
        </div>

        <div className="relative container-px py-32 lg:py-40 grid lg:grid-cols-12 gap-8 items-center text-white">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] bg-white/10 backdrop-blur-md border border-white/20 px-3 py-1.5 rounded-full">
              <MapPin className="w-3 h-3 text-secondary" strokeWidth={2} /> {language === "hi" ? "विशेष रूप से झारखंड के लिए" : (c.hero_badge || "Made only for Jharkhand")}
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] mt-6">
              {language === "hi" ? "जहाँ झारखंडी दिल" : (c.hero_title_1 || "Where Jharkhandi hearts")} <br className="hidden sm:block" />
              {language === "hi" ? "अपना हमसफर पाते हैं।" : (c.hero_title_2 || "find their forever.")}
            </h1>
            <p className="mt-6 text-lg lg:text-xl max-w-xl text-white/85 leading-relaxed">
              {language === "hi" ? t("landing.heroSubtitle") : (c.hero_subtitle || "A trusted matrimony platform exclusively for families across Ranchi, Jamshedpur, Dhanbad and all 24 districts. Verified profiles. Modern matching. Traditional values.")}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full px-7 h-12 btn-press" data-testid="hero-cta-register">
                <Link to="/register">{t("landing.ctaRegister")} <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-7 h-12 bg-white/10 border-white/30 text-white hover:bg-white/20 btn-press" data-testid="hero-cta-login">
                <Link to="/login">{language === "hi" ? "मेरा पहले से ही एक खाता है" : "I already have an account"}</Link>
              </Button>
            </div>

            <div className="mt-12 grid grid-cols-3 max-w-md gap-6">
              {c.stats && Array.isArray(c.stats) && c.stats.length > 0 ? (
                c.stats.map((st, i) => (
                  <div key={i}>
                    <div className="font-heading text-3xl font-medium text-secondary">
                      {st.n}
                    </div>
                    <div className="text-xs uppercase tracking-wider text-white/70 mt-1">{st.l}</div>
                  </div>
                ))
              ) : (
                <>
                  <div>
                    <div className="font-heading text-3xl font-medium text-secondary">
                      <AnimatedCounter value={stats.total_profiles} suffix="+" />
                    </div>
                    <div className="text-xs uppercase tracking-wider text-white/70 mt-1">{t("landing.statsMembers")}</div>
                  </div>
                  <div>
                    <div className="font-heading text-3xl font-medium text-secondary">
                      <AnimatedCounter value={stats.districts_covered} />
                    </div>
                    <div className="text-xs uppercase tracking-wider text-white/70 mt-1">{language === "hi" ? "कवर किए गए जिले" : "Districts covered"}</div>
                  </div>
                  <div>
                    <div className="font-heading text-3xl font-medium text-secondary">
                      <AnimatedCounter value={stats.blocks_covered} suffix="+" />
                    </div>
                    <div className="text-xs uppercase tracking-wider text-white/70 mt-1">{language === "hi" ? "कवर किए गए ब्लॉक" : "Blocks covered"}</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Trust band */}
      <section className="bg-card border-y border-border/60">
        <div className="container-px py-6 flex flex-wrap items-center justify-center gap-x-12 gap-y-3 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" strokeWidth={1.5}/>{language === "hi" ? "100% सत्यापित प्रोफ़ाइल" : "100% Verified Profiles"}</span>
          <span className="inline-flex items-center gap-2"><Heart className="w-4 h-4 text-primary" strokeWidth={1.5}/>{language === "hi" ? "पारिवारिक वैवाहिक मिलान" : "Family-friendly Matching"}</span>
          <span className="inline-flex items-center gap-2"><Star className="w-4 h-4 text-primary" strokeWidth={1.5}/>{language === "hi" ? "कुंडली और अनुकूलता" : "Kundli & Compatibility"}</span>
          <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" strokeWidth={1.5}/>{language === "hi" ? "झारखंड के लिए विशेष" : "Local to Jharkhand"}</span>
        </div>
      </section>

      {/* Bringing People Together */}
      <section className="bg-[#FAF6F7] py-20 border-b border-border/40">
        <div className="container-px text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold text-[#2A0A10]">
            {language === "hi" ? <>दिलों को <span className="text-secondary">पास लाना</span></> : <>Bringing People <span className="text-secondary">Together</span></>}
          </h2>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto text-base">
            {language === "hi" ? "हम सुरक्षा और विश्वसनीयता सुनिश्चित करने के लिए पारंपरिक पारिवारिक मूल्यों को आधुनिक सत्यापन मानकों के साथ जोड़ते हैं।" : "We combine traditional Indian family values with modern verification standards to create a secure, trustworthy space."}
          </p>

          <div className="mt-14 grid sm:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-8 rounded-2xl border border-border/40 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 text-left">
              <div className="w-12 h-12 rounded-full bg-[#FFF0F2] flex items-center justify-center mb-6">
                <ShieldCheck className="w-6 h-6 text-secondary" strokeWidth={2} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#2A0A10]">{language === "hi" ? "100% जाँचे गए प्रोफाइल" : "100% Screened Profiles"}</h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                {language === "hi" ? "स्वचालित प्रणालियों और मध्यस्थों द्वारा सत्यापित सक्रिय प्रोफाइलों में से स्थान, समुदाय, पेशे आदि के आधार पर खोजें।" : "Search by location, community, profession & more from lakhs of active profiles verified by our automated systems and moderators."}
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-border/40 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 text-left">
              <div className="w-12 h-12 rounded-full bg-[#FFF0F2] flex items-center justify-center mb-6">
                <CheckCircle className="w-6 h-6 text-secondary" strokeWidth={2} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#2A0A10]">{language === "hi" ? "भौतिक रूप से सत्यापित प्रोफाइल" : "Verifications by Personal Visit"}</h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                {language === "hi" ? "हमारे स्थानीय प्रतिनिधियों द्वारा भौतिक रूप से सत्यापित किए गए प्रोफाइलों की विशेष सूची के साथ मानसिक शांति पाएं।" : "Get peace of mind with special listings for profiles physically visited and verified by our local field representatives."}
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-border/40 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 text-left">
              <div className="w-12 h-12 rounded-full bg-[#FFF0F2] flex items-center justify-center mb-6">
                <Lock className="w-6 h-6 text-secondary" strokeWidth={2} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#2A0A10]">{language === "hi" ? "गोपनीयता पर पूरा नियंत्रण" : "Control over Privacy"}</h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                {language === "hi" ? "चुनें कि आपके संपर्क विवरण, फ़ोटो और कुंडली कौन देख सकता है। अवांछित मेल को ब्लॉक करें।" : "Choose who sees your contact details, photos, and horoscope. Block unwanted matches or restrict visibility to accepted connections."}
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* Features */}
      <section className="container-px py-20 lg:py-28 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <span className="label-cap">{t("landing.whyTitle")}</span>
          <h2 className="font-heading text-4xl sm:text-5xl text-primary mt-3">{language === "hi" ? "झारखंड के लिए, झारखंडियों द्वारा।" : "Built for Jharkhand,\nby Jharkhandis."}</h2>
          <p className="mt-5 text-muted-foreground leading-relaxed text-base">
            {language === "hi" ? "हर सुविधा हमारे समुदाय के अनुकूल है — जिलों, धर्मों और परंपराओं में। जाति फिल्टर से लेकर कुंडली मिलान तक, हम सभी का ध्यान रखते हैं।" : "Every feature is designed for our communities — across districts, faiths and traditions. From caste-aware filters to Kundli compatibility, we keep families involved while modern UX keeps the experience effortless."}
          </p>
        </div>
        <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
          {(c.features || [
            { title: language === "hi" ? "जिला-प्रथम खोज" : "District-first search", desc: language === "hi" ? "झारखंड के सभी 24 जिलों के अनुसार फ़िल्टर करें और वह जीवनसाथी खोजें जो अपने घर जैसा लगे।" : "Filter by all 24 Jharkhand districts and find matches who feel like home." },
            { title: language === "hi" ? "परिवार का विवरण सबसे पहले" : "Family details upfront", desc: language === "hi" ? "पारिवारिक प्रकार, माता-पिता के व्यवसाय, भाई-बहनों का विवरण साझा करें — जो वास्तव में मायने रखता है।" : "Share family type, parents' occupation, siblings — what truly matters." },
            { title: language === "hi" ? "अष्टकूट कुंडली मिलान" : "Ashtakoot Kundli match", desc: language === "hi" ? "पारंपरिक परिवारों के लिए अंतर्निहित अनुकूलता स्कोरिंग।" : "Built-in compatibility scoring for traditional families." },
            { title: language === "hi" ? "सत्यापित और सुरक्षित" : "Verified & private", desc: language === "hi" ? "फ़ोटो गोपनीयता, केवल प्रीमियम सदस्यों के लिए संपर्क प्रकट करना और सत्यापित खाते।" : "Photo privacy, premium-only contact reveal and verified accounts." },
          ]).map((f, i) => {
            const Icon = featIcons[i % featIcons.length];
            return (
              <div key={i} className="card-soft p-6">
                <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Free Bio-Data Builder CTA ──────────────────────────────────────── */}
      <section className="container-px py-14">
        <div className="rounded-3xl bg-gradient-to-r from-primary/10 via-accent to-secondary/10 border border-primary/20 p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
          <div className="flex-1">
            <span className="label-cap inline-flex items-center gap-1.5">
              ✨ {language === "hi" ? "निःशुल्क टूल" : "Free Tool — No Login Required"}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-primary mt-3">
              {language === "hi" ? "मैट्रिमोनियल बायो-डेटा बिल्डर" : "Matrimonial Bio-Data Builder"}
            </h2>
            <p className="mt-3 text-muted-foreground leading-relaxed max-w-lg">
              {language === "hi"
                ? "अपना खूबसूरत विवाह बायो-डेटा बनाएं — 6 रंग थीम, सेक्शन रिऑर्डर और PDF डाउनलोड। बिल्कुल मुफ्त!"
                : "Create a beautiful matrimonial bio-data in minutes. Choose from 6 colour themes, reorder sections by dragging, and download as PDF — completely free, no account needed!"}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="/biodata"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-white shadow hover:bg-primary/90 transition-colors"
              >
                {language === "hi" ? "अभी बनाएं" : "Create Bio-Data Free"} →
              </a>
            </div>
          </div>
          {/* Mini preview illustration */}
          <div className="shrink-0 hidden md:flex flex-col gap-2 bg-white rounded-2xl shadow-lg p-5 w-56 border border-border">
            <div className="h-6 rounded-lg bg-primary/80 w-full" />
            <div className="h-2 rounded bg-muted w-3/4 mt-1" />
            <div className="h-2 rounded bg-muted w-full" />
            <div className="h-2 rounded bg-muted w-5/6" />
            <div className="h-5 rounded-md bg-secondary/60 w-full mt-2" />
            <div className="h-2 rounded bg-muted w-full" />
            <div className="h-2 rounded bg-muted w-4/5" />
            <div className="h-5 rounded-md bg-secondary/60 w-full mt-2" />
            <div className="h-2 rounded bg-muted w-full" />
            <div className="h-2 rounded bg-muted w-3/4" />
            <div className="mt-3 text-center text-[9px] text-muted-foreground tracking-wider">hamarsathi.com</div>
          </div>
        </div>
      </section>

      {/* ── Personalised Matchmaking Service ─────────────────── */}
      <section className="bg-white py-20 border-t border-border/40">
        <div className="container-px">
          {/* Label + heading */}
          <div className="text-center mb-10">
            <span className="label-cap inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> {language === "hi" ? "व्यक्तिगत मैच-मेकिंग सेवा" : "Personalised Match-Making Service"}
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-[#2A0A10] mt-3">
              {language === "hi" ? <>प्रस्तुत है <span className="text-secondary italic">एक्सक्लूसिव</span></> : <>Introducing <span className="text-secondary italic">Exclusive</span></>}
            </h2>
          </div>

          <div className="relative rounded-3xl overflow-hidden max-w-4xl mx-auto shadow-xl">
            <img
              src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&auto=format&fit=crop"
              alt="Relationship Manager Consultation"
              className="w-full h-72 md:h-96 object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2A0A10]/50 to-transparent" />
            {/* Exclusive badge */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <span className="bg-primary text-white text-xs font-bold uppercase tracking-[0.25em] px-5 py-2 rounded-sm shadow-lg">
                {language === "hi" ? "एक्सक्लूसिव" : "Exclusive"}
              </span>
            </div>
          </div>

          {/* Three feature points */}
          <div className="grid sm:grid-cols-3 gap-8 mt-12 max-w-4xl mx-auto">
            {[
              {
                icon: <svg className="w-6 h-6 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2h5M12 12a4 4 0 100-8 4 4 0 000 8z"/></svg>,
                title: language === "hi" ? "अपने रिलेशनशिप मैनेजर से मिलें" : "Meet Your Relationship Manager",
                desc: language === "hi" ? "हमारे अनुभवी सलाहकार से जुड़ें जो आपकी प्रोफाइल का प्रबंधन करते हैं।" : "Connect with our highly experienced advisor who manages your profile.",
              },
              {
                icon: <svg className="w-6 h-6 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z"/></svg>,
                title: language === "hi" ? "अपनी प्राथमिकताएं साझा करें" : "Communicate Your Preferences",
                desc: language === "hi" ? "एक विस्तृत परामर्श यह समझने के लिए कि आप अपने संभावित जीवनसाथी में क्या तलाश रहे हैं।" : "Consultation to understand your expectations in a prospective partner.",
              },
              {
                icon: <svg className="w-6 h-6 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-3-3H5a3 3 0 00-3 3v2h5M15 7a3 3 0 11-6 0 3 3 0 016 0zM5 20v-1a4 4 0 014-4h6a4 4 0 014 4v1"/></svg>,
                title: language === "hi" ? "चुनिंदा प्रोफाइलों में से चुनें" : "Choose from Handpicked Profiles",
                desc: language === "hi" ? "हम ऑफ़लाइन बातचीत के लिए आपकी कसौटी पर खरी उतरने वाली प्रोफाइलों को शॉर्टलिस्ट करते हैं।" : "We shortlist profiles matching your criteria for offline interactions.",
              },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-3 sm:items-start sm:text-left">
                <div className="w-12 h-12 rounded-full bg-[#FFF0F2] flex items-center justify-center shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-heading text-lg font-semibold text-[#2A0A10]">{item.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-b from-white to-[#FAF6F7] py-20 border-t border-border/40">
        <div className="container-px grid lg:grid-cols-12 gap-12 items-center">
          {/* Left Column */}
          <div className="lg:col-span-7 space-y-6 text-left">
            <span className="label-cap">{language === "hi" ? "जुड़े रहें" : "Stay Connected"}</span>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold text-[#2A0A10] leading-tight">
              {language === "hi" ? "हमरसफर ऐप डाउनलोड करें" : "Download the Hamarsathi app"}
            </h2>
            <p className="text-muted-foreground text-base max-w-xl">
              {language === "hi" ? "अपने जीवनसाथी से कभी भी, कहीं भी जुड़ें। त्वरित सूचनाएं प्राप्त करें, चलते-फिरते चैट करें और पूरे झारखंड में सत्यापित प्रोफाइल खोजें।" : "Connect with your matches anytime, anywhere. Receive instant notifications, chat on the go, and search for verified profiles across Jharkhand."}
            </p>

            {/* App Badges & QR Code */}
            <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center py-4">
              {/* Badges */}
              <div className="flex flex-col gap-3 w-full sm:w-auto">
                {/* Google Play */}
                <button
                  id="playstore-install-btn"
                  onClick={(e) => installPWA(e, "android")}
                  className="flex items-center gap-3 bg-black hover:bg-zinc-900 text-white px-5 py-2.5 rounded-xl border border-zinc-800 transition-all duration-300 hover:scale-105 hover:shadow-lg w-fit"
                >
                  <Play className="w-6 h-6 fill-white text-white" />
                  <div className="text-left leading-none">
                    <span className="text-[10px] uppercase font-light tracking-wider">{language === "hi" ? "इसे प्राप्त करें" : "Get it on"}</span>
                    <div className="text-sm font-semibold mt-0.5">Google Play</div>
                  </div>
                </button>

                {/* App Store */}
                <button
                  id="appstore-install-btn"
                  onClick={(e) => installPWA(e, "ios")}
                  className="flex items-center gap-3 bg-black hover:bg-zinc-900 text-white px-5 py-2.5 rounded-xl border border-zinc-800 transition-all duration-300 hover:scale-105 hover:shadow-lg w-fit"
                >
                  <svg className="w-6 h-6 fill-white" viewBox="0 0 170 170">
                    <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.34.13-9.14-1.92-14.36-6.15-3.32-2.7-7.23-7.55-11.75-14.56-9.97-15.66-14.97-32.77-14.97-51.33 0-15.42 4.1-28.05 12.3-37.9 8.21-9.85 18.02-14.88 29.44-15.08 4.67-.07 9.87 1.25 15.58 3.93 5.71 2.68 9.56 3.53 11.53 2.56 2.68-1.24 7.21-2.84 13.58-4.8 6.37-1.96 11.98-2.61 16.83-1.96 15.12 2.29 26.43 8.15 33.93 17.62-12.83 7.82-19.08 18.15-18.77 31.02.31 10.38 4.18 19.04 11.62 26 7.45 6.96 16.48 10.74 27.12 11.33-2.22 6.07-4.53 11.6-6.9 16.63zm-24.16-98.81c0-7.85 2.8-15.34 8.4-22.47 5.6-7.13 12.56-11.65 20.9-13.56.12.85.18 1.77.18 2.76 0 7.44-2.84 14.82-8.52 22.13-5.68 7.32-12.71 11.96-21.1 13.91-.45-1.04-.68-2.02-.68-2.77z" />
                  </svg>
                  <div className="text-left leading-none">
                    <span className="text-[10px] uppercase font-light tracking-wider">{language === "hi" ? "डाउनलोड करें" : "Download on the"}</span>
                    <div className="text-sm font-semibold mt-0.5">App Store</div>
                  </div>
                </button>

                {/* PWA Install */}
                <button
                  id="pwa-install-btn"
                  onClick={(e) => installPWA(e)}
                  className="flex items-center gap-3 bg-primary hover:bg-primary/90 text-white px-5 py-2.5 rounded-xl border border-primary/60 transition-all duration-300 hover:scale-105 hover:shadow-lg w-fit"
                >
                  <svg className="w-6 h-6 fill-white" viewBox="0 0 24 24"><path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1 14v-4H7l5-6 5 6h-4v4h-2z"/></svg>
                  <div className="text-left leading-none">
                    <span className="text-[10px] uppercase font-light tracking-wider">{language === "hi" ? "इंस्टॉल करें" : "Install as"}</span>
                    <div className="text-sm font-semibold mt-0.5">Web App (PWA)</div>
                  </div>
                </button>
              </div>

              {/* QR Code Container */}
              <div className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-border/60 shadow-sm">
                <div className="p-2 bg-slate-50 rounded-xl border border-slate-100">
                  <QrCode className="w-16 h-16 text-[#2A0A10]" strokeWidth={1.5} />
                </div>
                <div className="text-left">
                  <div className="text-sm font-semibold text-[#2A0A10]">{language === "hi" ? "डाउनलोड करने के लिए स्कैन करें" : "Scan to download"}</div>
                  <div className="text-xs text-muted-foreground mt-1 max-w-[120px]">
                    {language === "hi" ? "त्वरित इंस्टॉल करने के लिए अपने कैमरा को इंगित करें" : "Point your camera to scan and install instantly"}
                  </div>
                </div>
              </div>
            </div>

            {/* Link Sender Form */}
            <div className="max-w-md bg-white p-5 rounded-2xl border border-border/60 shadow-sm space-y-3">
              <div className="text-sm font-medium text-[#2A0A10]">
                {language === "hi" ? "या अपने ईमेल पर डाउनलोड लिंक प्राप्त करें" : "Or get a download link on your Email"}
              </div>
              {linkSent ? (
                <div className="p-3 bg-green-50 text-green-700 text-xs rounded-xl font-medium flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  {language === "hi" ? "लिंक भेजा गया! अपना इनबॉक्स जांचें — इंस्टॉल करने के लिए इसे अपने फोन पर खोलें।" : "Link sent! Check your inbox — open it on your phone to install."}
                </div>
              ) : (
                <form onSubmit={handleSendLink} className="flex gap-2">
                  <div className="relative flex-grow">
                    <input
                      type="email"
                      placeholder={language === "hi" ? "अपना ईमेल एड्रेस दर्ज करें" : "Enter your email address"}
                      value={contactInput}
                      onChange={(e) => { setContactInput(e.target.value); setLinkError(""); }}
                      className="w-full px-4 py-2 bg-slate-50 border border-border/80 rounded-xl text-sm focus:outline-none focus:border-secondary transition-all"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={linkSending}
                    className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-xl px-5 h-[38px] text-xs font-semibold disabled:opacity-60"
                  >
                    {linkSending ? (language === "hi" ? "भेजा जा रहा है…" : "Sending…") : (language === "hi" ? "लिंक प्राप्त करें" : "Get Link")}
                  </Button>
                </form>
              )}
              {linkError && <div className="text-red-500 text-xs">{linkError}</div>}
            </div>
          </div>

          {/* Right Column (Phone Mockup) */}
          <div className="lg:col-span-5 flex justify-center relative py-10">
            {/* Phone Outline */}
            <div className="w-[300px] h-[580px] bg-zinc-950 rounded-[3rem] p-3 shadow-2xl border-[6px] border-zinc-800 relative overflow-hidden ring-[12px] ring-zinc-900">
              {/* Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-zinc-950 rounded-b-2xl z-20 flex items-center justify-center">
                <div className="w-3 h-3 bg-zinc-900 rounded-full" />
              </div>

              {/* Inside Screen */}
              <div className="w-full h-full bg-slate-50 rounded-[2.5rem] overflow-hidden relative flex flex-col pt-6 font-sans">
                {/* Header Mock */}
                <div className="px-4 py-3 bg-white border-b border-slate-100 flex items-center justify-between">
                  <div className="font-semibold text-xs tracking-wider text-[#2A0A10]">My Matches</div>
                  <div className="flex gap-1.5">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                    <div className="w-1.5 h-1.5 bg-slate-300 rounded-full" />
                  </div>
                </div>

                {/* Profile Card Mock */}
                <div className="flex-grow p-4 flex flex-col justify-end relative bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=500&auto=format&fit=crop')` }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />

                  {/* Profile info inside the card */}
                  <div className="relative text-white z-10 space-y-1 text-left">
                    <div className="flex items-center gap-1">
                      <div className="font-semibold text-sm">Priya Sharma, 27</div>
                      <div className="w-3.5 h-3.5 bg-secondary text-secondary-foreground rounded-full flex items-center justify-center text-[8px] font-bold">✓</div>
                    </div>
                    <div className="text-[10px] text-white/80">Software Engineer · Ranchi</div>
                    <div className="text-[10px] bg-white/20 backdrop-blur-md px-2 py-0.5 rounded-full w-fit">Kayastha</div>
                  </div>
                </div>

                {/* Navigation Mock */}
                <div className="h-10 bg-white border-t border-slate-100 grid grid-cols-4 items-center justify-items-center text-slate-400 text-xs">
                  <span className="text-secondary">🏠</span>
                  <span>👥</span>
                  <span>💬</span>
                  <span>👤</span>
                </div>
              </div>
            </div>

            {/* Floating Glassmorphism Badges */}
            <div className="absolute top-1/4 -left-6 bg-white/85 backdrop-blur-md border border-white/40 shadow-lg px-4 py-2.5 rounded-2xl flex items-center gap-2 animate-bounce z-20" style={{ animationDuration: '3s' }}>
              <span className="text-green-600 text-sm">🛡️</span>
              <div className="text-left">
                <div className="text-[10px] font-semibold text-[#2A0A10]">Easy Verification</div>
                <div className="text-[8px] text-muted-foreground">Self & Field Verified</div>
              </div>
            </div>

            <div className="absolute bottom-1/3 -right-6 bg-white/85 backdrop-blur-md border border-white/40 shadow-lg px-4 py-2.5 rounded-2xl flex items-center gap-2 animate-bounce z-20" style={{ animationDuration: '4s' }}>
              <span className="text-rose-500 text-sm">📞</span>
              <div className="text-left">
                <div className="text-[10px] font-semibold text-[#2A0A10]">Voice & Video Calls</div>
                <div className="text-[8px] text-muted-foreground">Connect securely in-app</div>
              </div>
            </div>

            <div className="absolute bottom-12 -left-4 bg-white/85 backdrop-blur-md border border-white/40 shadow-lg px-4 py-2.5 rounded-2xl flex items-center gap-2 animate-bounce z-20" style={{ animationDuration: '5s' }}>
              <span className="text-secondary text-sm">✨</span>
              <div className="text-left">
                <div className="text-[10px] font-semibold text-[#2A0A10]">100k+ downloads</div>
                <div className="text-[8px] text-muted-foreground">Active</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── JharkhandVivah Mitra Program Showcase Section ───────────────────── */}
      <section className="py-20 bg-gradient-to-b from-[#FFFDF9] via-[#FFF5F6] to-white relative overflow-hidden border-y border-border/40">
        <div className="container-px relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-amber-500/10 text-amber-900 border border-amber-500/20 text-xs font-bold uppercase tracking-wider mb-3">
              {language === "hi" ? "🏆 जिला और प्रखंड स्तर" : "🏆 District & Block Level"}
            </span>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-primary font-black tracking-tight">
              {language === "hi" ? "झारखंडविवाह मित्र कार्यक्रम" : "JharkhandVivah Mitra Program"}
            </h2>
            <p className="text-muted-foreground mt-4 text-base leading-relaxed">
              {language === "hi"
                ? "झारखंड के सभी 24 जिलों के लिए आधिकारिक सामुदायिक राजदूत प्रणाली। समाज को डिजिटल माध्यम से जोड़ने की एक विशेष पहल।"
                : "An exclusive community ambassador system serving all 24 districts of Jharkhand — connecting families with trust and care."}
            </p>
          </div>

          {/* Highlights 4-Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-border/80 shadow-sm space-y-3 hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center font-bold text-xl">
                🛡️
              </div>
              <h3 className="font-black text-xl text-primary tracking-tight">
                {language === "hi" ? "प्रशासनिक नियुक्ति" : "Admin Appointed"}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {language === "hi"
                  ? "केवल एडमिन द्वारा नियुक्त आधिकारिक जिला एंबेसडर। प्रामाणिकता और गोपनीयता की गारंटी।"
                  : "Officially appointed by Admin only. Ensuring complete trust and authentic local representation."}
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-border/80 shadow-sm space-y-3 hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-700 flex items-center justify-center font-bold text-xl">
                📱
              </div>
              <h3 className="font-black text-xl text-emerald-800 tracking-tight">
                {language === "hi" ? "मासिक ₹299 रीचार्ज" : "₹299 Monthly Recharge"}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {language === "hi"
                  ? "हर महीने 5+ प्रोफाइल जोड़ने पर Jio/Airtel/Vi का ₹299 का फ्री मोबाइल रीचार्ज।"
                  : "Guaranteed ₹299 monthly mobile recharge on bringing 5+ valid referrals every month."}
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-border/80 shadow-sm space-y-3 hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-2xl bg-amber-500/10 text-amber-700 flex items-center justify-center font-bold text-xl">
                🎟️
              </div>
              <h3 className="font-black text-xl text-amber-900 tracking-tight">
                {language === "hi" ? "₹200 डिस्काउंट कूपन" : "₹200 Discount Coupons"}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {language === "hi"
                  ? "5 रेफरल पूरा होने पर ₹200 का रिडीम करने योग्य कूपन कोड + 1 महीना फ्री प्रीमियम!"
                  : "Earn ₹200 discount coupon code + 1 Month Free Premium on hitting 5 referral milestone!"}
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-border/80 shadow-sm space-y-3 hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-2xl bg-purple-500/10 text-purple-700 flex items-center justify-center font-bold text-xl">
                🏅
              </div>
              <h3 className="font-black text-xl text-purple-900 tracking-tight">
                {language === "hi" ? "डिस्ट्रिक्ट एंबेसडर बैज" : "District Ambassador Badge"}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {language === "hi"
                  ? "प्रोफ़ाइल पर आधिकारिक 'District Ambassador' बैज एवं टॉप लीडरबोर्ड पहचान।"
                  : "Official District Ambassador badge on profile + digital certificate & leaderboard recognition."}
              </p>
            </div>
          </div>

          {/* 24 Districts Banner */}
          <div className="mt-12 p-8 bg-white rounded-3xl border border-border/80 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center md:text-left">
              <div className="font-black text-2xl text-primary flex items-center justify-center md:justify-start gap-2 tracking-tight">
                <MapPin className="w-6 h-6 text-secondary" />
                {language === "hi" ? "झारखंड के सभी 24 जिलों में सक्रिय सेवा" : "Serving All 24 Districts of Jharkhand"}
              </div>
              <p className="text-xs text-muted-foreground max-w-xl">
                Ranchi, Dhanbad, East Singhbhum (Jamshedpur), Hazaribagh, Bokaro, Deoghar, Giridih, Dumka, Ramgarh, Palamu, West Singhbhum, Chatra, Koderma, Sahibganj, Pakur, Jamtara, Latehar, Simdega, Khunti, Gumla, Lohardaga, Godda, Garhwa, Saraikela Kharsawan.
              </p>
            </div>
            <Button
              onClick={() => { setMitraModalOpen(true); setApplyStep(1); setApplyOtp(""); setApplyErr(""); setApplyMsg(""); }}
              className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md transition-all h-auto"
            >
              📝 {language === "hi" ? "विवाह मित्र हेतु आवेदन करें" : "Apply for Vivah Mitra Appointment"}
            </Button>
          </div>

          {/* Block-Level Leaderboard */}
          {blockLeaderboard?.filter(item => item.total_joins > 0).length > 0 && (
            <div className="mt-20 border-t border-border/40 pt-16">
              <div className="text-center max-w-2xl mx-auto">
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] bg-[#FFF0F2] text-secondary border border-secondary/20 px-3 py-1.5 rounded-full font-semibold">
                  <Trophy className="w-3.5 h-3.5 text-secondary animate-pulse" /> {language === "hi" ? "सक्रियता लीडरबोर्ड" : "ACTIVITY LEADERBOARD"}
                </span>
                <h3 className="font-heading text-3xl font-semibold text-[#2A0A10] mt-4">
                  {language === "hi" ? "शीर्ष सक्रिय प्रखंड (Top Active Blocks)" : "Top Active Blocks"}
                </h3>
                <p className="mt-3 text-muted-foreground text-xs leading-relaxed">
                  {language === "hi" 
                    ? "झारखंड के इन प्रखंडों से सबसे अधिक संख्या में लोग जुड़ रहे हैं एवं विवाह मित्र सक्रिय हैं।" 
                    : "These blocks across Jharkhand are leading in new member registrations and active Vivah Mitras."}
                </p>
              </div>

              <div className="mt-10 flex flex-wrap justify-center gap-4 max-w-6xl mx-auto">
                {blockLeaderboard.filter(item => item.total_joins > 0).slice(0, 8).map((item, idx) => (
                  <div
                    key={`${item.district}-${item.block}`}
                    className="bg-white p-4 rounded-2xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between text-sm w-full sm:w-[260px] shrink-0"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${
                        idx === 0 ? "bg-amber-400 text-amber-950" : idx === 1 ? "bg-slate-300 text-slate-800" : idx === 2 ? "bg-amber-700/20 text-amber-900" : "bg-muted text-muted-foreground"
                      }`}>
                        #{idx + 1}
                      </span>
                      <div>
                        <div className="font-bold text-[#2A0A10]">{item.block}</div>
                        <div className="text-[11px] text-muted-foreground">{item.district}</div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="font-bold text-emerald-600 block">{item.total_joins} Joins</span>
                      <span className="text-[10px] text-muted-foreground">{item.mitras_count} Mitra(s)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA strip */}
      <section className="container-px pb-24">
        <div className="relative overflow-hidden rounded-3xl">
          <img src="https://images.unsplash.com/photo-1722952934708-749c22eb2e58" alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 to-primary/70" />
          <div className="relative p-10 lg:p-16 text-primary-foreground grid lg:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="font-heading text-4xl lg:text-5xl">{c.cta_title || "Your forever begins with one step."}</h3>
              <p className="mt-4 text-primary-foreground/85 max-w-md">{c.cta_subtitle || "Join thousands of Jharkhandi families. Registration is free."}</p>
            </div>
            <div className="flex lg:justify-end">
              <Button asChild size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full h-12 px-7" data-testid="cta-bottom-register">
                <Link to="/register">Create Free Profile <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Membership Plans ──────────────────────────────────── */}
      <section className="py-20 bg-background">
        <div className="container-px">
          {/* Heading */}
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="label-cap inline-flex items-center gap-1.5">
              <Crown className="w-3.5 h-3.5" /> Membership Plans
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-primary mt-3">
              Find your perfect match faster
            </h2>
            <p className="text-muted-foreground mt-4 text-base leading-relaxed">
              Upgrade your plan as per your needs. With a paid membership you can seamlessly
              connect with your prospects and get more responses.
            </p>
          </div>

          {/* Cards */}
          <div className="flex flex-col lg:flex-row gap-6 items-stretch justify-center">

            {/* FREE card */}
            <div className="w-full lg:w-72 bg-white rounded-2xl border border-border/60 shadow-sm p-8 flex flex-col">
              <div className="w-6 h-0.5 bg-muted-foreground/40 mb-5" />
              <div className="font-heading text-2xl text-foreground mb-6">Free</div>
              <ul className="space-y-3 flex-1">
                {[
                  { text: "Browse Profiles", ok: true },
                  { text: "Shortlist & Send Interest", ok: true },
                  { text: "Message & chat with unlimited users", ok: true },
                  { text: "Get up to 3x more matches daily", ok: false },
                  { text: "Unlock access to advanced search", ok: false },
                  { text: "View contact details", ok: false },
                  { text: "Make unlimited voice and video calls", ok: false },
                  { text: "Get 3 free Spotlights", ok: false },
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    {f.ok
                      ? <Check className="w-4 h-4 text-secondary mt-0.5 shrink-0" />
                      : <X className="w-4 h-4 text-muted-foreground/40 mt-0.5 shrink-0" />}
                    <span className={f.ok ? "text-foreground" : "text-muted-foreground/60"}>{f.text}</span>
                  </li>
                ))}
              </ul>
              <Button asChild variant="outline" className="mt-8 rounded-full border-primary text-primary hover:bg-primary hover:text-white">
                <Link to="/register">Register Free</Link>
              </Button>
            </div>

            {/* PAID plans — from API or fallback */}
            {(packages.length > 0 ? packages.filter(p => p.price > 0) : [

              { id: "f1", name: "Silver", price: 499, duration_days: 30, description: "Try premium for 30 days.", featured: false, tag: null,
                features: ["Browse Profiles", "Shortlist & Send Interest", "Message & chat with unlimited users", "Get up to 3x more matches daily", "Unlock access to advanced search", "View contact details", "Make unlimited voice and video calls", "Get 3 free Spotlights"] },
              { id: "f2", name: "Gold", price: 1299, duration_days: 90, description: "Best value for serious search.", featured: true, tag: "Most Popular",
                features: ["Browse Profiles", "Shortlist & Send Interest", "Message & chat with unlimited users", "Get up to 3x more matches daily", "Unlock access to advanced search", "View contact details", "Make unlimited voice and video calls", "Get 3 free Spotlights"] },
              { id: "f3", name: "Diamond", price: 3999, duration_days: 365, description: "For families committed to finding the one.", featured: false, tag: null,
                features: ["Browse Profiles", "Shortlist & Send Interest", "Message & chat with unlimited users", "Get up to 3x more matches daily", "Unlock access to advanced search", "View contact details", "Make unlimited voice and video calls", "Get 3 free Spotlights"] },
            ]).map((pkg) => (
              <div
                key={pkg.id}
                className={`w-full lg:w-72 rounded-2xl p-8 flex flex-col shadow-md transition hover:shadow-xl
                  ${ pkg.featured
                      ? "bg-primary text-white ring-4 ring-primary/30 scale-[1.03] z-10"
                      : "bg-white border border-border/60" }`}
              >
                <div className={`w-6 h-0.5 mb-5 ${ pkg.featured ? "bg-white/50" : "bg-muted-foreground/40" }`} />

                {pkg.tag && (
                  <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold mb-2
                    ${ pkg.featured ? "text-white/70" : "text-secondary" }`}>
                    {pkg.tag}
                  </span>
                )}

                <div className={`font-heading text-2xl mb-1 ${ pkg.featured ? "text-white" : "text-foreground" }`}>
                  {pkg.name}
                </div>

                <div className="flex items-baseline gap-1 mb-1">
                  <span className={`font-heading text-4xl font-bold ${ pkg.featured ? "text-white" : "text-foreground" }`}>
                    ₹{pkg.price.toLocaleString()}
                  </span>
                  <span className={`text-sm ${ pkg.featured ? "text-white/60" : "text-muted-foreground" }`}>
                    / {pkg.duration_days >= 365 ? "year" : pkg.duration_days >= 60 ? `${Math.round(pkg.duration_days / 30)} months` : "month"}
                  </span>
                </div>

                {pkg.description && (
                  <p className={`text-xs mb-6 ${ pkg.featured ? "text-white/70" : "text-muted-foreground" }`}>
                    {pkg.description}
                  </p>
                )}

                <ul className="space-y-3 flex-1">
                  {(pkg.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${ pkg.featured ? "text-white" : "text-secondary" }`} />
                      <span className={pkg.featured ? "text-white/90" : "text-foreground"}>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  asChild
                  className={`mt-8 rounded-full font-semibold
                    ${ pkg.featured
                        ? "bg-white text-primary hover:bg-white/90"
                        : "bg-primary text-white hover:bg-primary/90" }`}
                >
                  <Link to="/app/premium">Browse Membership Plans</Link>
                </Button>
              </div>
            ))}
          </div>

          {/* Bottom note */}
          <p className="text-center text-xs text-muted-foreground mt-10">
            Secured by Razorpay &middot; UPI, Cards, Net Banking &amp; Wallets supported.
          </p>
        </div>
      </section>

      {/* Modal Dialog: Apply for Vivah Mitra */}
      {mitraModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 space-y-4 shadow-2xl border border-slate-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                🏆 {language === "hi" ? "विवाह मित्र आवेदन फॉर्म" : "Apply for Vivah Mitra Ambassador"}
              </h2>
              <button onClick={() => setMitraModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold text-sm">
                ✕
              </button>
            </div>

            {applyMsg ? (
              <div className="p-4 bg-emerald-50 text-emerald-900 border border-emerald-200 rounded-2xl text-xs space-y-3">
                <div className="font-bold flex items-center gap-2 text-emerald-800 text-sm">
                  <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                  {language === "hi" ? "आवेदन जमा हुआ!" : "Application Submitted!"}
                </div>
                <p className="leading-relaxed">{applyMsg}</p>
                <Button onClick={() => setMitraModalOpen(false)} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs rounded-xl font-bold">
                  {language === "hi" ? "ठीक है" : "Close"}
                </Button>
              </div>
            ) : applyStep === 1 ? (
              <form onSubmit={handleSendMitraOtp} className="space-y-4 text-xs">
                <p className="text-slate-500 leading-relaxed">
                  {language === "hi"
                    ? "कृपया अपना विवरण भरें। केवल वैध व्हाट्सएप नंबर से ही आवेदन स्वीकार किया जाएगा।"
                    : "Fill your details below. Only valid WhatsApp numbers verified via OTP will be accepted."}
                </p>

                {applyErr && (
                  <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded-xl font-medium text-[11px]">
                    ⚠️ {applyErr}
                  </div>
                )}

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">
                    {language === "hi" ? "पूरा नाम *" : "Full Name *"}
                  </label>
                  <input
                    type="text"
                    required
                    placeholder={language === "hi" ? "उदा. रमेश कुमार शर्मा" : "e.g. Ramesh Kumar Sharma"}
                    value={applyName}
                    onChange={(e) => setApplyName(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-emerald-600"
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">
                    {language === "hi" ? "वैध व्हाट्सएप नंबर *" : "Valid WhatsApp Number *"}
                  </label>
                  <input
                    type="tel"
                    required
                    maxLength={10}
                    placeholder={language === "hi" ? "उदा. 9835760000" : "e.g. 9835760000"}
                    value={applyPhone}
                    onChange={(e) => setApplyPhone(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-emerald-600 font-mono"
                  />
                  <span className="text-[10px] text-slate-400 mt-1 block">
                    {language === "hi" ? "केवल 10-अंकों का सक्रिय व्हाट्सएप नंबर (OTP द्वारा सत्यापित किया जाएगा)" : "10-digit active WhatsApp number (Verified via OTP)"}
                  </span>
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">
                    {language === "hi" ? "जिला (District) *" : "District *"}
                  </label>
                  <select
                    value={applyDistrict}
                    onChange={(e) => setApplyDistrict(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-emerald-600"
                  >
                    {JHARKHAND_DISTRICTS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">
                    {language === "hi" ? "प्रखंड (Block) *" : "Block *"}
                  </label>
                  <select
                    value={applyBlock}
                    onChange={(e) => setApplyBlock(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-emerald-600"
                  >
                    {(JHARKHAND_BLOCKS[normalizeDistrictName(applyDistrict)] || []).map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">
                    {language === "hi" ? "संदेश / नोट (ऐच्छिक)" : "Brief Message (Optional)"}
                  </label>
                  <textarea
                    rows={2}
                    placeholder={language === "hi" ? "आप इस प्रोग्राम में क्यों जुड़ना चाहते हैं?" : "Why do you want to join as a Vivah Mitra?"}
                    value={applyNotes}
                    onChange={(e) => setApplyNotes(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-emerald-600"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setMitraModalOpen(false)} className="text-xs rounded-xl">
                    {language === "hi" ? "रद्द करें" : "Cancel"}
                  </Button>
                  <Button type="submit" disabled={applying} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-xl font-bold">
                    {applying ? (language === "hi" ? "OTP भेजा जा रहा है..." : "Sending OTP...") : (language === "hi" ? "OTP प्राप्त करें" : "Send Verification OTP")}
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleVerifyMitraOtp} className="space-y-4 text-xs">
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-2xl text-emerald-900 text-xs space-y-1">
                  <p className="font-bold flex items-center gap-1.5">
                    📲 {language === "hi" ? "OTP कोड भेजा गया" : "OTP Code Sent"}
                  </p>
                  <p className="text-[11px] text-emerald-800 leading-relaxed">
                    {language === "hi"
                      ? `आपके नंबर +91-${applyPhone} पर 6-अंकों का वेरिफिकेशन OTP भेजा गया है।`
                      : `A 6-digit OTP has been sent to your number +91-${applyPhone}.`}
                  </p>
                  {applyOtpHint && (
                    <div className="mt-2 pt-2 border-t border-emerald-200/80 text-[11px] font-mono text-emerald-950 font-bold bg-white/80 p-2 rounded-xl border border-emerald-300 flex items-center justify-between">
                      <span>🔑 {language === "hi" ? "सत्यापन कोड:" : "Verification Code:"} <span className="text-base tracking-widest text-emerald-700 font-black ml-1">{applyOtpHint}</span></span>
                      <button type="button" onClick={() => setApplyOtp(applyOtpHint)} className="text-[10px] bg-emerald-700 text-white px-2 py-0.5 rounded-lg hover:bg-emerald-800 font-sans">
                        Auto-fill
                      </button>
                    </div>
                  )}
                </div>

                {applyErr && (
                  <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded-xl font-medium text-[11px]">
                    ⚠️ {applyErr}
                  </div>
                )}

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">
                    {language === "hi" ? "6-अंकों का OTP कोड दर्ज करें *" : "Enter 6-Digit OTP Code *"}
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={6}
                    placeholder="e.g. 639201"
                    value={applyOtp}
                    onChange={(e) => setApplyOtp(e.target.value)}
                    className="w-full px-4 py-2.5 text-center font-mono text-base font-bold bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-emerald-600 tracking-widest"
                  />
                </div>

                <div className="flex justify-between items-center text-[11px] text-slate-500 pt-1">
                  <button
                    type="button"
                    onClick={() => { setApplyStep(1); setApplyErr(""); }}
                    className="text-emerald-700 underline font-semibold hover:text-emerald-800"
                  >
                    ✏️ {language === "hi" ? "नंबर बदलें" : "Change Number"}
                  </button>
                  <button
                    type="button"
                    onClick={handleSendMitraOtp}
                    disabled={applying}
                    className="text-slate-600 underline font-semibold hover:text-slate-900"
                  >
                    🔄 {language === "hi" ? "दोबारा OTP भेजें" : "Resend OTP"}
                  </button>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setMitraModalOpen(false)} className="text-xs rounded-xl">
                    {language === "hi" ? "रद्द करें" : "Cancel"}
                  </Button>
                  <Button type="submit" disabled={applying} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-xl font-bold">
                    {applying ? (language === "hi" ? "सत्यापित हो रहा है..." : "Verifying...") : (language === "hi" ? "सत्यापित करें एवं आवेदन जमा करें" : "Verify & Submit Application")}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
